import { useEffect } from 'react';
import { usePendingEmailConfirmation } from '@/hooks/usePendingEmailConfirmation';
import { useAuthContext } from '@/contexts/auth';

/**
 * NavSpacer - Placeholder div to compensate for fixed navbar + banners
 * 
 * This component creates a spacer that occupies the space taken by:
 * - MainNav: 64px (h-16)
 * - EmailConfirmationBanner: 40px (when visible)
 * 
 * Base Total: 64px (h-16)
 * With Email Banner: 104px
 * 
 * Using a DOM element instead of body padding-top prevents Radix UI
 * dropdown/select scroll issues when opening menus in a fixed navbar.
 * 
 * IMPORTANT: This component exposes the CSS variable --fixed-header-offset
 * on :root (document.documentElement) for global access by sticky elements.
 */
export function NavSpacer() {
  const { pendingEmail } = usePendingEmailConfirmation();
  const { isAuthenticated, user } = useAuthContext();
  
  // Show extra space when email confirmation banner is visible
  // Case 1: pre-signup (pending email in localStorage, not authenticated)
  // Case 2: authenticated user with unverified email
  const showEmailBanner = (!!pendingEmail && !isAuthenticated) || (isAuthenticated && !!user && !user.emailConfirmedAt);
  
  // Calculate the fixed header offset for sticky elements
  const headerOffset = showEmailBanner ? '104px' : '64px';
  
  // Propagate CSS variable to :root for global access by sibling elements
  useEffect(() => {
    document.documentElement.style.setProperty('--fixed-header-offset', headerOffset);
    
    return () => {
      // Reset to default on unmount
      document.documentElement.style.setProperty('--fixed-header-offset', '64px');
    };
  }, [headerOffset]);
  
  return (
    <div 
      className={`nav-spacer w-full transition-all duration-200 ${showEmailBanner ? 'h-[104px]' : 'h-16'}`}
      style={{ '--fixed-header-offset': headerOffset } as React.CSSProperties}
      aria-hidden="true"
    />
  );
}
